import { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useSearchParams, useNavigate } from "react-router-dom";
import ApiProvider from "../../apiProvider/addToCartApi"
import getApi from "../../apiProvider/api"
import CheckoutApiProvider from "../../apiProvider/checkoutApi";
import { Country } from 'country-state-city';
import { IMAGE_URL } from "../../network/apiClient";
import { useTranslation } from "../../context/TranslationContext";
import { useCurrency } from "../../context/CurrencyContext";
import './myaccount.css';
import { Helmet } from 'react-helmet-async';

const countryList = Country.getAllCountries();

const CheckoutSchema = Yup.object().shape({
    firstName: Yup.string()
        .matches(/^[A-Za-z\s]+$/, 'Full name must contain only alphabets')
        .required('Full name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    phoneNo: Yup.string()
        .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits')
        .required('Phone no is required'),
    address1: Yup.string().required('Address is required'),
    zipCode: Yup.string()
        .matches(/^\d{6}$/, 'Zip code must be exactly 6 digits')
        .required('Zip code is required'),
    paymentMethod: Yup.string().required("Please select a payment method"),
    country: Yup.string().required("Country is required"),
    city: Yup.string()
        .matches(/^[A-Za-z\s]+$/, 'City must contain only alphabets')
        .required("City is required"),
    state: Yup.string().required("State is required"),
});

const CheckoutPage = () => {
    const navigate = useNavigate();
    const { translateSync } = useTranslation();
    const { currency, getCurrencySymbol, getProductPrice, getProductMrp } = useCurrency();
    const [searchParams] = useSearchParams();
    const type = searchParams.get("type");

    // User & Cart State
    const [userDetails, setUserDetails] = useState({});
    const [cartListDetails, setCartListDetails] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [courseDetails, setCourseDetails] = useState({}); // Legacy support if needed

    // Payment State
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [currencys, setCurrencys] = useState([]);
    const [transectionCurrecy, settransectionCurrecy] = useState({});

    // UI State
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalContent, setModalContent] = useState("");

    const policies = {
        cancellation: { title: "Cancellation & Refund Policy", content: "Policy Content..." },
        terms: { title: "Terms & Conditions", content: "Terms Content..." },
        privacy: { title: "Privacy Policy", content: "Privacy Content..." }
    };

    const openPolicyModal = (policyKey) => {
        setModalTitle(policies[policyKey]?.title || "Policy");
        setModalContent(policies[policyKey]?.content || "Content not available.");
        setShowModal(true);
    };

    // --- Data Fetching ---
    const getCartDetails = async () => {
        const token = localStorage.getItem('userToken');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user._id || user.id;
        const guestId = localStorage.getItem('guestUserId');

        let result;
        if (token && userId) {
            result = await ApiProvider.getCart(userId, { type: 'user', userType: 'user' });
        } else if (guestId) {
            result = await ApiProvider.getCart(guestId, { type: 'guest', userType: 'guest' });
        }
        console.log(result, "result-cart page");

        if (result && result.status && result.response) {
            const responseData = result.response.data || result.response;
            const cartData = Array.isArray(responseData) ? responseData[0] : responseData;
            console.log(cartData, "cartData--");

            if (cartData && cartData.products) {
                const mappedCart = cartData.products.map(item => {
                    const cartAttributes = item.attributes || {};
                    const matchingRow = item.customerAttribute?.rowData?.find(row => {
                        return Object.entries(cartAttributes).every(([key, val]) => {
                            const rowKey = Object.keys(row).find(k => {
                                const kLower = k.toLowerCase();
                                const keyLower = key.toLowerCase();
                                if (kLower === keyLower) return true;
                                if ((kLower === 'color' || kLower === 'colour') && (keyLower === 'color' || keyLower === 'colour')) return true;
                                return false;
                            });
                            return rowKey && String(row[rowKey]) === String(val);
                        });
                    });

                    // Stock Logic
                    const isOutOfStock = !matchingRow || (matchingRow.stock <= 0);

                    const attrImg = matchingRow?.images?.[0] || item.customerAttribute?.rowData?.[0]?.images?.[0];

                    return {
                        id: item.productCartId,
                        cartItemId: item.productCartId,
                        productId: item._id,
                        productName: item.productName || "Unknown Product",
                        price: getProductMrp(item, matchingRow),
                        offerPrice: getProductPrice(item, matchingRow),
                        quantity: item.quantity,
                        attributes: item.attributes || {},
                        isOutOfStock: isOutOfStock,
                        categoryImage: (() => {
                            if (attrImg) {
                                return `${attrImg.docPath}/${attrImg.docName}`;
                            }
                            return item.productImage?.[0]?.docName ? `${item.productImage[0].docPath}/${item.productImage[0].docName}` : null;
                        })()
                    };
                });

                // Filter out OOS items entirely for Checkout page as per user request
                const inStockItems = mappedCart.filter(item => !item.isOutOfStock);

                const total = inStockItems.filter(item => item.offerPrice !== null).reduce((sum, item) => sum + (item.offerPrice * item.quantity), 0);

                setCartListDetails(inStockItems);
                setTotalPrice(total);

                // Save details to sessionStorage for purchase callback mapping
                const orderGAData = {
                    items: inStockItems.map(item => ({
                        id: item.productId,
                        name: item.productName,
                        subcategory: item.subcategory || 'silk',
                        price: item.offerPrice || 0,
                        quantity: item.quantity
                    })),
                    total: total,
                    currency: currency,
                    shipping: 0,
                    tax: 0
                };
                sessionStorage.setItem('pending_checkout_items', JSON.stringify(orderGAData));

                if (window.gtag) {
                    let totalSGD = total;
                    if (currency !== 'SGD') {
                        totalSGD = Math.round(total / 60) || 1;
                    }
                    const gaItems = inStockItems.map(item => {
                        let itemPriceSGD = item.offerPrice || 0;
                        if (currency !== 'SGD') {
                            itemPriceSGD = Math.round(itemPriceSGD / 60) || 1;
                        }
                        return {
                            item_id: item.productId,
                            item_name: item.productName,
                            item_category: 'Saree',
                            item_category2: item.subcategory || 'silk',
                            price: itemPriceSGD,
                            quantity: item.quantity
                        };
                    });
                    window.gtag('event', 'begin_checkout', {
                        currency: 'SGD',
                        value: totalSGD,
                        items: gaItems
                    });
                }
            }
        }
    };

    const getUserAddressDetails = async () => {
        try {
            const result = await ApiProvider.getUserDetails();
            if (result.status && result.response) {
                setUserDetails(result.response.data.userDetail || {});
            }
        } catch (error) {
            console.error("Error fetching user details:", error);
        }
    };

    const getPaymentMethods = async () => {
        const result = await getApi.getPaymentMethod();
        if (result?.response?.data?.data) {
            setPaymentMethods(result.response.data.data);
        }
    };

    const [savedAddresses, setSavedAddresses] = useState([]);

    const fetchSavedAddresses = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const userId = user._id || user.id;
            if (userId) {
                const result = await getApi.getAddress(userId, 'customer');
                if (result && result.status && result.response?.data) {
                    setSavedAddresses(result.response.data);
                }
            }
        } catch (error) {
            console.error("Error fetching saved addresses:", error);
        }
    };

    useEffect(() => {
        getCartDetails();
    }, [currency]);

    useEffect(() => {
        getUserAddressDetails();
        getPaymentMethods();
        fetchSavedAddresses();
        window.scrollTo(0, 0);
    }, []);

    // --- Form Submission ---
    const formSubmit = async (values) => {
        if (!cartListDetails || cartListDetails.length === 0) {
            alert("No in-stock items to order. Please go back to the cart.");
            return;
        }
        const hasUnavailableCurrency = cartListDetails.some(item => item.offerPrice === null);
        if (hasUnavailableCurrency) {
            alert("Some items are not available in the selected currency. Please return to the Cart or switch currency.");
            return;
        }
        try {
            const token = localStorage.getItem('userToken');
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const guestId = localStorage.getItem('guestUserId');

            // Determine User ID (Prefer logged-in user, fallback to guest)
            // Note: Backend requires explicit handling for Guest IDs if they aren't ObjectIds.
            // For now, we pass what we have. API provider will handle assignment.
            const resolvedUserId = user._id || user.id;
            const userId = (token && resolvedUserId) ? resolvedUserId : guestId;

            const input = {
                userId: userId, // Pass the resolved ID
                firstName: values.firstName,
                email: values.email,
                phoneNo: values.phoneNo,
                address1: values.address1,
                country: values.country,
                city: values.city,
                state: values.state,
                zipCode: values.zipCode,
                paymentMethod: values.paymentMethod,
                items: cartListDetails,
                amount: totalPrice,
                currency: currency,
                type: type,
                isGuest: !token // Helper flag to determine model
            };

            const result = await CheckoutApiProvider.checkout(input);
            console.log(result, "Checkout Response");

            if (result && result.status) {
                if (values.paymentMethod === 'Cash') {
                    // COD Success - Redirect to Success Page
                    navigate("/myaccount?tab=orders");
                } else if (result.response?.data?.paymentUrl) {
                    // Online Payment - Redirect to Payment Gateway
                    window.location.href = result.response.data.paymentUrl;
                } else {
                    navigate("/myaccount?tab=orders");
                }
            } else {
                alert("Order creation failed. Please try again.");
            }
        } catch (error) {
            console.error("Checkout Error:", error);
        }
    };

    return (
        <div className="checkout-page-wrapper" style={{
            fontFamily: "'Lato', sans-serif",
            backgroundColor: "#ebe7e1",
            backgroundImage: "url('https://www.transparenttextures.com/patterns/natural-paper.png')",
            color: "#3a3530",
            minHeight: "100vh",
            paddingTop: "100px"
        }}>
            <Helmet>
                <title>Checkout | Colours Boutique</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            {/* Minimal Header */}
            <section className="py-4 text-center">
                <div className="container">
                    <h1 className="display-5 fw-bold text-uppercase" style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "2px", color: "#3a3530" }}>
                        {translateSync('Checkout')}
                    </h1>
                </div>
            </section>

            <div className="container pb-5">
                <Formik
                    enableReinitialize
                    initialValues={{
                        firstName: userDetails.name || "",
                        email: userDetails.email || '',
                        phoneNo: userDetails.phone || '',
                        address1: userDetails.address || '',
                        zipCode: userDetails.pincode || '',
                        country: userDetails.country || '',
                        city: userDetails.city || '',
                        state: userDetails.state || '',
                        paymentMethod: '',
                        acceptTerms: false
                    }}
                    validationSchema={CheckoutSchema}
                    onSubmit={formSubmit}
                >
                    {({ errors, touched, values, handleChange, handleBlur, setFieldValue, isValid, dirty }) => (
                        <Form>
                            <div className="row g-5">
                                {/* Left Column: Billing Details */}
                                <div className="col-lg-7">
                                    <div className="p-4 bg-white shadow-sm border rounded">
                                        <h4 className="mb-4 fw-bold text-uppercase" style={{ fontFamily: "'Playfair Display', serif" }}>
                                            {translateSync('Billing Details')}
                                        </h4>

                                        {savedAddresses.length > 0 && (
                                            <div className="mb-4 p-3 bg-light border rounded text-start">
                                                <label className="form-label fw-bold text-muted small text-uppercase mb-2">Use a Saved Address</label>
                                                <select
                                                    className="form-select"
                                                    onChange={(e) => {
                                                        const addrId = e.target.value;
                                                        if (addrId) {
                                                            const addr = savedAddresses.find(a => a._id === addrId);
                                                            if (addr) {
                                                                setFieldValue('firstName', addr.contactName || '');
                                                                setFieldValue('phoneNo', addr.contactNumber || '');
                                                                setFieldValue('address1', addr.addressLine || '');
                                                                setFieldValue('city', addr.city || '');
                                                                setFieldValue('state', addr.state || '');
                                                                setFieldValue('zipCode', addr.postalCode || '');
                                                                const countryObj = countryList.find(c => c.name === addr.country || c.isoCode === addr.country);
                                                                if (countryObj) {
                                                                    setFieldValue('country', countryObj.isoCode);
                                                                } else {
                                                                    setFieldValue('country', addr.country || '');
                                                                }
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <option value="">-- Select a Saved Address --</option>
                                                    {savedAddresses.map(addr => (
                                                        <option key={addr._id} value={addr._id}>
                                                            {addr.label} ({addr.contactName} - {addr.city})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        <div className="row g-3">
                                            <div className="col-12">
                                                <label className="form-label text-muted small text-uppercase">Full Name</label>
                                                <input
                                                    name="firstName"
                                                    className="form-control"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.firstName}
                                                    onKeyPress={(event) => {
                                                        if (!/[a-zA-Z\s]/.test(event.key)) {
                                                            event.preventDefault();
                                                        }
                                                    }}
                                                />
                                                {errors.firstName && (touched.firstName || values.firstName) && <div className="text-danger small text-start">{errors.firstName}</div>}
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label text-muted small text-uppercase">Email</label>
                                                <input name="email" className="form-control" onChange={handleChange} onBlur={handleBlur} value={values.email} />
                                                {errors.email && (touched.email || values.email) && <div className="text-danger small text-start">{errors.email}</div>}
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label text-muted small text-uppercase">Phone</label>
                                                <input
                                                    name="phoneNo"
                                                    className="form-control"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.phoneNo}
                                                    maxLength={10}
                                                    onKeyPress={(event) => {
                                                        if (!/[0-9]/.test(event.key)) {
                                                            event.preventDefault();
                                                        }
                                                    }}
                                                />
                                                {errors.phoneNo && (touched.phoneNo || values.phoneNo) && <div className="text-danger small text-start">{errors.phoneNo}</div>}
                                            </div>

                                            <div className="col-12">
                                                <label className="form-label text-muted small text-uppercase">Address</label>
                                                <textarea name="address1" className="form-control" rows="2" onChange={handleChange} onBlur={handleBlur} value={values.address1} />
                                                {errors.address1 && (touched.address1 || values.address1) && <div className="text-danger small text-start">{errors.address1}</div>}
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label text-muted small text-uppercase">City</label>
                                                <input
                                                    name="city"
                                                    className="form-control"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.city}
                                                    onKeyPress={(event) => {
                                                        if (!/[a-zA-Z\s]/.test(event.key)) {
                                                            event.preventDefault();
                                                        }
                                                    }}
                                                />
                                                {errors.city && (touched.city || values.city) && <div className="text-danger small text-start">{errors.city}</div>}
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label text-muted small text-uppercase">State</label>
                                                <input name="state" className="form-control" onChange={handleChange} onBlur={handleBlur} value={values.state} />
                                                {errors.state && (touched.state || values.state) && <div className="text-danger small text-start">{errors.state}</div>}
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label text-muted small text-uppercase">Postal Code</label>
                                                <input
                                                    name="zipCode"
                                                    className="form-control"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.zipCode}
                                                    maxLength={6}
                                                    onKeyPress={(event) => {
                                                        if (!/[0-9]/.test(event.key)) {
                                                            event.preventDefault();
                                                        }
                                                    }}
                                                />
                                                {errors.zipCode && (touched.zipCode || values.zipCode) && <div className="text-danger small text-start">{errors.zipCode}</div>}
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label text-muted small text-uppercase">Country</label>
                                                <select name="country" className="form-select" onChange={handleChange} onBlur={handleBlur} value={values.country}>
                                                    <option value="">Select Country</option>
                                                    {countryList.map((c) => (<option key={c.isoCode} value={c.isoCode}>{c.name}</option>))}
                                                </select>
                                                {errors.country && (touched.country || values.country) && <div className="text-danger small text-start">{errors.country}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column: Order Summary & Payment */}
                                <div className="col-lg-5">
                                    <div className="p-4 bg-white shadow-sm border rounded mb-4">
                                        <h4 className="mb-4 fw-bold text-uppercase" style={{ fontFamily: "'Playfair Display', serif" }}>
                                            {translateSync('Your Order')}
                                        </h4>

                                        <ul className="list-group mb-4">
                                            {cartListDetails.map((item, idx) => (
                                                <li key={idx} className="list-group-item d-flex justify-content-between align-items-center lh-sm py-3">
                                                    <div>
                                                        <h6 className="my-0">{translateSync(item.productName)}</h6>
                                                        <small className="text-muted">Qty: {item.quantity}</small>
                                                    </div>
                                                    <span className="text-muted ms-3" style={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                                                        {item.offerPrice === null ? (
                                                            <span className="text-danger small font-italic">Currency not available</span>
                                                        ) : (
                                                            `${getCurrencySymbol()} ${(item.offerPrice * item.quantity).toLocaleString()}`
                                                        )}
                                                    </span>
                                                </li>
                                            ))}
                                            <li className="list-group-item d-flex justify-content-between bg-light">
                                                <span>Total ({currency})</span>
                                                <strong>{getCurrencySymbol()} {totalPrice.toLocaleString()}</strong>
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="p-4 bg-white shadow-sm border rounded">
                                        <h4 className="mb-4 fw-bold text-uppercase" style={{ fontFamily: "'Playfair Display', serif" }}>
                                            {translateSync('Payment Method')}
                                        </h4>

                                        <div className="mb-3">
                                            {/* COD Option */}
                                            <div className="form-check p-3 border rounded mb-2" style={{ cursor: "pointer", backgroundColor: values.paymentMethod === 'Cash' ? '#f0f8ff' : '#fff', borderColor: values.paymentMethod === 'Cash' ? '#0d6efd' : '#dee2e6' }}>
                                                <input className="form-check-input" type="radio" name="paymentMethod" id="payment_cod" value="Cash"
                                                    onChange={() => setFieldValue('paymentMethod', 'Cash')} checked={values.paymentMethod === 'Cash'} />
                                                <label className="form-check-label w-100 fw-bold" htmlFor="payment_cod" style={{ cursor: "pointer" }}>
                                                    {translateSync('Cash on Delivery')}
                                                </label>
                                                {values.paymentMethod === 'Cash' && <small className="text-muted d-block mt-1">Pay with cash upon delivery.</small>}
                                            </div>

                                            {/* Other Methods */}
                                            {paymentMethods.map((method, idx) => (
                                                <div key={idx} className="form-check p-3 border rounded mb-2" style={{ cursor: "pointer", backgroundColor: values.paymentMethod === method.methodName ? '#f0f8ff' : '#fff' }}>
                                                    <input className="form-check-input" type="radio" name="paymentMethod" id={`payment_${idx}`} value={method.methodName}
                                                        onChange={() => setFieldValue('paymentMethod', method.methodName)} checked={values.paymentMethod === method.methodName} />
                                                    <label className="form-check-label w-100 fw-bold" htmlFor={`payment_${idx}`} style={{ cursor: "pointer" }}>
                                                        {method.methodName}
                                                    </label>
                                                </div>
                                            ))}

                                            {errors.paymentMethod && touched.paymentMethod && <div className="text-danger small text-start">{errors.paymentMethod}</div>}
                                        </div>

                                        {(() => {
                                            const hasUnavailableCurrency = cartListDetails.some(item => item.offerPrice === null);
                                            return (
                                                <>
                                                    <button type="submit" className="btn btn-dark w-100 py-3 text-uppercase fw-bold"
                                                        disabled={!isValid || hasUnavailableCurrency}
                                                        style={{
                                                            letterSpacing: "2px",
                                                            backgroundColor: hasUnavailableCurrency ? "#ccc" : undefined,
                                                            border: hasUnavailableCurrency ? "1px solid #ccc" : undefined,
                                                            cursor: hasUnavailableCurrency ? "not-allowed" : "pointer"
                                                        }}>
                                                        {hasUnavailableCurrency ? 'Unavailable Items in Cart' : (values.paymentMethod === 'Cash' ? 'Place Order' : 'Proceed to Payment')}
                                                    </button>
                                                    {hasUnavailableCurrency && (
                                                        <div className="alert alert-warning mt-3 rounded-0 py-2 small" role="alert" style={{ fontSize: '0.8rem' }}>
                                                            Please remove the unavailable items or change currency to complete your order.
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>

            {/* Modal for Policies (simplified) */}
            {showModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{modalTitle}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">{modalContent}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CheckoutPage;